import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { OutletService } from '../../services';
import { handleNucleusResponse } from '../../services/utils';
import type { UpdateOutletRequest } from '@billx/types';
import { getTokenManager } from '../../config/tokenManager';

import { useCurrentBusiness } from '../businesses/useCurrentBusiness';
import { useCurrentUser } from '../users/useCurrentUser';
import { useCurrentBusinessOutlets } from './useOutlets';

export const useCurrentOutlet = () => {
  const { currentUser, isLoading: userLoading } = useCurrentUser();
  const { currentBusiness, isLoading: businessLoading } = useCurrentBusiness();
  const businessId = currentBusiness?.id;
  const { outlets: accessibleOutlets, isLoading: isOutletsLoading } =
    useCurrentBusinessOutlets();

  const outletId =
    currentUser?.preferences?.selectedOutletId ?? accessibleOutlets?.[0]?.id;
  const queryClient = useQueryClient();

  const QUERY_KEY = ['currentOutlet', outletId];
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      if (!outletId) {
        throw new Error('No outlet selected');
      }
      const response = await OutletService.fetchOutletById(outletId);
      return handleNucleusResponse(response).data;
    },
    enabled: !!outletId && !!businessId && !userLoading && !businessLoading,
  });

  const updateOutlet = useMutation({
    mutationFn: (data: UpdateOutletRequest) => {
      if (!outletId) {
        throw new Error('No outlet selected');
      }
      return OutletService.updateOutlet(outletId, data);
    },
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.setQueryData(QUERY_KEY, () => response.data);
      } else {
        getTokenManager().showNotification({
          message: response.message,
          severity: 'error',
          duration: 5000,
        });
      }
    },
  });

  const { data: currentOutlet, isLoading, ...restQuery } = query;
  return {
    currentOutlet,
    updateOutlet,
    isLoading: isLoading || userLoading || businessLoading || isOutletsLoading,
    ...restQuery,
  };
};
